/* eslint-disable @typescript-eslint/no-explicit-any */

import { CsvCellValue, generateCSVContent, getZodErrorMessage } from '@airreading/common';
import Papa, { ParseStepResult } from 'papaparse';
import { z, ZodTypeAny } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';

export const STUDENTS_CSV_TEMPLATE_URL =
  'https://docs.google.com/spreadsheets/d/1xJovBqkai1WECe3EL-xNfjow83txRBrNxktTE1Jskwo/edit?usp=sharing';
export const BULK_UPDATE_LEARNING_PATH_CSV_TEMPLATE_URL =
  'https://docs.google.com/spreadsheets/d/18TyeQ9RsV3geI5A_xrP5xqDrOCoXXkRV0d63aiMdD38/edit?usp=sharing';
export const TEACHERS_CSV_TEMPLATE_URL =
  'https://docs.google.com/spreadsheets/d/1_tACJW-bRp0eUSCixjKD6O6IGYX1hMzN8yFIT_cfGIg/edit?usp=sharing';

export const csvFormSchema = z.object({
  csvFile: z
    .instanceof(FileList, { message: 'CSV is required' })
    .refine((files) => files.length > 0, { message: 'CSV is required' }),
});

export const createCsvStepsFunction = (rowSchema: ZodTypeAny, csvParsingErrors: string[]) => {
  let rowNumber = 0;

  return (results: ParseStepResult<unknown>) => {
    rowNumber += 1;
    const { data, errors } = results;

    if (errors.length > 0) {
      errors.forEach((error) => {
        csvParsingErrors.push(`Row ${rowNumber}: ${error.message}`);
      });

      return;
    }

    const zodParsingResult = rowSchema.safeParse(convertEmptyStringsToNull(data as AnyObject));
    if (!zodParsingResult.success) {
      csvParsingErrors.push(`Row ${rowNumber}: ${getZodErrorMessage(zodParsingResult.error)}`);
    }
  };
};

type AnyObject = { [key: string]: unknown };

function convertEmptyStringsToNull(obj: AnyObject): AnyObject {
  const newObj: AnyObject = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      newObj[key] = obj[key] === '' ? null : obj[key];
    }
  }

  return newObj;
}

export function downloadCSV(content: string, filename = 'export.csv') {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
}

export function exportToCSV<T>(rows: T[], columns: CsvColumn<T>[], filename = 'export.csv') {
  if (!rows.length) {
    throw new TypeError('No data to export');
  }
  const csvContent = generateCSVContent(rows, columns);
  downloadCSV(csvContent, filename);
}

function normalizeCsvValue(value: unknown): CsvCellValue {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return value;
  }
  return null;
}

function hasAccessorKey<T>(col: ColumnDef<T, unknown>): col is ColumnDef<T, unknown> & { accessorKey: string } {
  return (
    col != null &&
    typeof col === 'object' &&
    'accessorKey' in col &&
    typeof (col as { accessorKey?: unknown }).accessorKey === 'string'
  );
}

function hasAccessorFn<T>(
  col: ColumnDef<T, any>,
): col is ColumnDef<T, any> & { accessorFn: (row: T, index: number) => unknown } {
  return typeof (col as any).accessorFn === 'function';
}

export type CsvColumn<T> = {
  id: string;
  getValue: (row: T, index: number) => CsvCellValue;
};

export type OverrideFn<T> = (row: T, index: number) => CsvCellValue;

function valueFromRowUsingAccessor<T>(row: T, accessor: string): unknown {
  return accessor.split('.').reduce((acc: unknown, key) => {
    if (acc && typeof acc === 'object' && key in (acc as object)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, row);
}

/**
 * Builds a simplified list of columns for client-side CSV export based on currently loaded table data.
 *
 * Limitations:
 * - Only works with data already loaded in memory (current pagination/page).
 * - Does not perform server-side fetching for full dataset exports.
 * - Does not enforce full type-safety for ColumnDef accessor keys.
 *
 * For full dataset exports, consider implementing a server-side CSV export API.
 */
export function buildClientExportColumns<T>(
  columns: ColumnDef<T, any>[],
  overrides: Record<string, OverrideFn<T>> = {},
): CsvColumn<T>[] {
  return columns
    .filter((col) => col.id && col.id !== 'select' && col.id !== 'selection')
    .map((col) => {
      const columnId = col.header && typeof col.header === 'string' ? col.header : col.id ?? '';

      return {
        id: columnId,
        getValue: (row: T, index: number) => {
          if (col.id && overrides[col.id]) {
            return overrides[col.id](row, index);
          }

          try {
            if (hasAccessorKey(col)) {
              const val = valueFromRowUsingAccessor(row, col.accessorKey);
              return normalizeCsvValue(val);
            }

            if (hasAccessorFn(col)) {
              return normalizeCsvValue(col.accessorFn(row, index));
            }
          } catch {
            return null;
          }

          if (columnId && typeof row === 'object' && row !== null && columnId in row) {
            return normalizeCsvValue((row as Record<string, unknown>)[columnId]);
          }

          return null;
        },
      };
    });
}

export function parseCsvFile<T = unknown>(file: File): Promise<T[]> {
  return new Promise((resolve, reject) => {
    Papa.parse<T>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => resolve(result.data),
      error: reject,
    });
  });
}

interface ParseCsvOnUploadError {
  row: number;
  message: string;
}

interface ParseCsvOnUploadResult {
  errors: Array<ParseCsvOnUploadError>;
  warnings?: Array<ParseCsvOnUploadError>;
  successRowsCount: number;
  createdRowsCount?: number;
  updatedRowsCount?: number;
}

interface ParseCsvOnToastOptions {
  title: string;
  description?: string;
}

export interface ParseCsvAndUploadOptions<T extends z.ZodTypeAny> {
  file: File;
  rowSchema: T;
  onUpload: (file: File) => Promise<ParseCsvOnUploadResult>;
  onToast: (options: ParseCsvOnToastOptions) => void;
  onSetErrors: (errors: string[]) => void;
  onSetWarnings?: (warnings: string[]) => void;
  skipEmptyLines?: boolean;
}

export interface ParseCsvAndUploadResult {
  successRowsCount: number;
  createdRowsCount?: number;
  updatedRowsCount: number;
  hasErrors: boolean;
  hasWarnings: boolean;
}

export function parseCsvAndUpload<T extends z.ZodTypeAny>({
  file,
  rowSchema,
  onUpload,
  onToast,
  onSetErrors,
  onSetWarnings,
  skipEmptyLines = true,
}: ParseCsvAndUploadOptions<T>): Promise<ParseCsvAndUploadResult> {
  const csvParsingErrors: string[] = [];
  onSetErrors([]);
  onSetWarnings?.([]);

  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines,
      step: createCsvStepsFunction(rowSchema, csvParsingErrors),
      error: (error) => {
        onToast({ title: 'CSV parse error', description: error.message });
        reject(error);
      },
      async complete() {
        if (csvParsingErrors.length > 0) {
          onToast({ title: 'Errors while parsing CSV' });
          onSetErrors(csvParsingErrors);
          reject(csvParsingErrors);
          return;
        }

        try {
          const { successRowsCount, createdRowsCount, updatedRowsCount, errors, warnings = [] } = await onUpload(file);

          if (errors.length > 0) {
            const formattedErrors = errors.map(({ row, message }) => `Row ${row}: ${message}`);
            onSetErrors(formattedErrors);
            onToast({ title: 'Some rows failed to upload. See error messages in the modal.' });
          }

          if (warnings.length > 0) {
            const formattedWarnings = warnings.map(({ row, message }) => `Row ${row}: ${message}`);
            onSetWarnings?.(formattedWarnings);
          }

          resolve({
            successRowsCount,
            createdRowsCount,
            updatedRowsCount: updatedRowsCount ?? 0,
            hasErrors: errors.length > 0,
            hasWarnings: warnings.length > 0,
          });
        } catch (error) {
          onToast({ title: 'Error while uploading CSV' });
          reject(error);
        }
      },
    });
  });
}
